/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { retrieveAddress } from '../../services/api/orders'
import { Address } from '../../model'
import ErrorMessage from '../ErrorMessage/ErrorMessage'
import { useLocalAddress } from '../../hooks/useLocalAddress'

interface AddressFormProps {
    defaultValues?: Address
    onChange(values: any, isValid: boolean): void
}

const AddressForm: React.FunctionComponent<AddressFormProps> = ({ defaultValues, onChange }: AddressFormProps) => {
    const { getLocalAddress } = useLocalAddress() // Initialize with empty object to avoid undefined issues

    const { register, control, getValues, setValue, formState, trigger } = useForm({
        defaultValues: defaultValues,
        mode: 'onChange',
        reValidateMode: 'onChange'
    })
    const [loadingAddress, setLoadingAddress] = useState(false)
    const [localAddressAvailable, setLocalAddressAvailable] = useState(false)

    const values = useWatch({ control }) // Always current form values
    useEffect(() => {
        onChange?.(values, formState.isValid)
    }, [values, formState.isValid])

    const localAddress = getLocalAddress()

    useEffect(() => {
        if (localAddress) {
            setLocalAddressAvailable(true)
        }
    }, [localAddress])
    const copyAddressPrevOrder = () => {
        setLoadingAddress(true)
        setValue('zipcode', localAddress.zipcode)
        setValue('houseNumber', localAddress.houseNumber)
        setValue('extension', localAddress.extension)
        setValue('street', localAddress.street)
        setValue('city', localAddress.city)
        setValue('surface', localAddress.surface)
        trigger(['zipcode', 'houseNumber', 'street', 'city', 'surface'])
        setLoadingAddress(false)
    }

    const tryAdressAutocomplete = async () => {
        const { zipcode, houseNumber, extension, city, street } = getValues()

        trigger(['zipcode', 'houseNumber'])

        if (!zipcode || !houseNumber) {
            return
        }
        setLoadingAddress(true)
        try {
            // get autocomplete results
            const res = await retrieveAddress({ zipcode, houseNumber, extension })

            // Only overwrite values if not already filled
            if (res.city && !city) {
                setValue('city', res.city)
            }
            trigger('city')
            if (res.street && !street) {
                setValue('street', res.street)
            }
            trigger('street')
            if (res.extension && !extension) {
                setValue('extension', res.extension)
            }
            trigger('extension')

            // Set surface always
            setValue('surface', res.surface)
            trigger('surface')
        } catch (e) {
            console.log(e)
        }
        setLoadingAddress(false)
    }

    return (
        <>
            <form className='flex flex-col w-full md:w-1/2 space-y-4'>
                <h2 className='font-medium text-lg mb-0'>Adresgegevens</h2>
                {localAddressAvailable && (
                    <div className='flex flex-grow bgPrimaryColor bg-opacity-15 rounded text-[--tb-mainHeading] text-sm px-3 py-2 justify-between items-center'>
                        <p className='font-bold'>
                            Recent adres:{' '}
                            <span className='font-normal'>
                                {localAddress.street} {localAddress.houseNumber}, {localAddress.zipcode}{' '}
                                {localAddress.city}
                            </span>
                        </p>
                        <button
                            type='button'
                            className='font-bold text-[--tb-primary] hover:no-underline block underline cursor-pointer m-0'
                            onClick={copyAddressPrevOrder}
                        >
                            Overnemen
                        </button>
                    </div>
                )}
                <div className='flex flex-wrap md:flex-nowrap space-y-4 md:space-y-0 md:space-x-4 mb-1'>
                    <div className='w-full flex-grow'>
                        <label className='text-sm font-medium text-[--tb-mainHeading] mb-1 block'>Postcode</label>
                        <input
                            {...register('zipcode', { required: 'Postcode is verplicht' })}
                            placeholder='Postcode'
                            type={'text'}
                            className='w-full'
                            onBlur={() => tryAdressAutocomplete()}
                            disabled={loadingAddress}
                        />
                        <ErrorMessage message={formState.errors.zipcode?.message} />
                    </div>
                    <div className='flex-grow w-full'>
                        <label className='text-sm font-medium text-[--tb-mainHeading] mb-1 block'>Huisnummer</label>
                        <input
                            {...register('houseNumber', { required: 'Huisnummer is verplicht', valueAsNumber: true })}
                            placeholder='Huisnummer'
                            type={'number'}
                            className='w-full rounded focus:outline-none px-3 py-1.5'
                            min={1}
                            onBlur={() => tryAdressAutocomplete()}
                            disabled={loadingAddress}
                        />
                        <ErrorMessage message={formState.errors.houseNumber?.message} />
                    </div>
                    <div className='flex-shrink'>
                        <label className='text-sm font-medium text-[--tb-mainHeading] mb-1 block'>Extensie</label>
                        <input
                            {...register('extension')}
                            placeholder='A'
                            type={'text'}
                            className='w-full'
                            onBlur={() => tryAdressAutocomplete()}
                            disabled={loadingAddress}
                        />
                    </div>
                </div>
                <div className='flex flex-col space-y-1'>
                    <label className='text-sm font-medium text-[--tb-mainHeading] mb-1 block'>Straat</label>
                    <input
                        {...register('street', { required: 'Straat is verplicht' })}
                        disabled={loadingAddress}
                        placeholder='Voorbeeldlaan'
                        type={'text'}
                        className='mb-1'
                    />
                    <ErrorMessage message={formState.errors.street?.message} />
                </div>
                <div className='flex flex-col space-y-1'>
                    <label className='text-sm font-medium text-[--tb-mainHeading] mb-1 block'>Plaats</label>
                    <input
                        {...register('city', { required: 'Plaats is verplicht' })}
                        disabled={loadingAddress}
                        placeholder='Voorbeeldplaats'
                        type={'text'}
                        className='mb-1'
                    />
                    <ErrorMessage message={formState.errors.city?.message} />
                </div>
                <div className='flex flex-col space-y-1'>
                    <label className='text-sm font-medium text-[--tb-mainHeading] mb-1 block'>
                        Woonoppervlakte in m²
                    </label>
                    {values.surface ? (
                        <input
                            {...register('surface')}
                            placeholder='aantal in m²'
                            type={'number'}
                            className='mb-1'
                            disabled
                            readOnly
                        />
                    ) : (
                        <span className='text-[--tb-primary] text-sm'>Onbekend</span>
                    )}
                    <ErrorMessage message={formState.errors.surface?.message} />
                </div>
            </form>
        </>
    )
}

export default AddressForm
